/**
 * Health of the NETWORK path to the static database, and nothing else.
 *
 * In `online` static-data mode every reference read is an HTTP round-trip to the `haniverse-worker` D1
 * proxy, so a flaky connection turns an otherwise-fine app into an indefinite spinner. This module
 * is the one place that observes those round-trips, so the UI can say "still loading — your
 * connection looks slow" and offer offline mode.
 *
 * WHAT MAY REPORT HERE. Only the worker-query transport (`static-database.server.ts`, shared by the
 * native `online` mode and the web build). Local SQLite opens, migrations, FSRS scans and every
 * other slow-but-local step must NOT: a slow local query is a performance bug to fix at its source,
 * and telling the user their connection is bad would be both wrong and useless. This narrowness is
 * the whole point — the degraded banner exists to explain a genuinely external failure, so it must
 * be unreachable during healthy (if slow) local work.
 *
 * Deliberately dependency-free and framework-free: it is imported by the transport, which is
 * bundled into the workerd/Node server as well as the app, so it can hold no React and no
 * React Native imports.
 */

/**
 * How long a single worker round-trip may take before the connection counts as degraded.
 *
 * A healthy worker query is tens of milliseconds locally and well under a second over the network
 * (the transport's own retry budget is ~1.75s across 4 attempts). This is set well above that so
 * ordinary latency — including a retry or two — never trips it; what it catches is the connection
 * that is effectively not working.
 */
export const DEGRADED_STATIC_NETWORK_MS = 4000;

export interface StaticNetworkHealth {
  /** True once a request has failed outright, or exceeded {@link DEGRADED_STATIC_NETWORK_MS}. */
  isDegraded: boolean;
  /** Why it is degraded, for copy: a hard failure reads differently from mere slowness. */
  reason: "failed" | "slow" | null;
}

const HEALTHY: StaticNetworkHealth = { isDegraded: false, reason: null };

let health: StaticNetworkHealth = HEALTHY;
const listeners = new Set<() => void>();

function setHealth(next: StaticNetworkHealth): void {
  if (health.isDegraded === next.isDegraded && health.reason === next.reason) {
    return;
  }
  health = next;
  for (const listener of listeners) {
    listener();
  }
}

/** Current health. Identity-stable while unchanged, so it is safe for `useSyncExternalStore`. */
export function getStaticNetworkHealth(): StaticNetworkHealth {
  return health;
}

export function subscribeToStaticNetworkHealth(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * A worker query completed. `durationMs` is the whole call including retries, so a request that
 * only succeeded after several attempts still reports as slow.
 *
 * A fast success clears a previous degradation: the connection came back, so the banner must go
 * away on its own without the user doing anything.
 */
export function reportStaticNetworkSuccess(durationMs: number): void {
  if (durationMs >= DEGRADED_STATIC_NETWORK_MS) {
    setHealth({ isDegraded: true, reason: "slow" });
    return;
  }
  setHealth(HEALTHY);
}

/** A worker query failed after exhausting its retries — the connection is not working. */
export function reportStaticNetworkFailure(): void {
  setHealth({ isDegraded: true, reason: "failed" });
}

/** Test-only: forget observed health so each test starts from a healthy connection. */
export function resetStaticNetworkHealthForTests(): void {
  health = HEALTHY;
  listeners.clear();
}
